import {
  ChangeDetectionStrategy,
  ChangeDetectorRef,
  Component,
  EventEmitter,
  Input,
  Output,
  SimpleChanges,
} from '@angular/core';

import { UrlActionTriggerPipe } from '@recruitee/common';
import { InfoboxComponent } from '@recruitee/components/infobox';
import { I18nService, TranslatePipe } from '@recruitee/i18n';
import { IntercomService } from '@recruitee/intercom-integration';
import type { PricingCompanySize } from '@recruitee/pricing-types';
import { PricingCompanySizeE, Currency } from '@recruitee/pricing-types';
import type { PaymentInterval } from '@recruitee/subscription-types';
import {
  SegmentedButtonType,
  SelectOption,
  SegmentedControlComponent,
  SelectComponent,
  RtTooltip,
  IconComponent,
  BadgeComponent,
  CheckboxComponent,
  HeadingComponent,
} from '@recruitee/user-interface';

export enum SizeSelectMode {
  Default = 'default',
  Locked = 'locked',
  LockedMinimalMissing = 'lockedMinimalMissing',
  Agency = 'agency',
}

@Component({
  selector: 'rt-plan-picker-2024-toolbar',
  templateUrl: './plan-picker-2024-toolbar.component.html',
  styleUrls: ['./plan-picker-2024-toolbar.component.less'],
  changeDetection: ChangeDetectionStrategy.OnPush,
  imports: [
    HeadingComponent,
    CheckboxComponent,
    BadgeComponent,
    IconComponent,
    RtTooltip,
    SelectComponent,
    InfoboxComponent,
    SegmentedControlComponent,
    TranslatePipe,
    UrlActionTriggerPipe,
  ],
})
export class PlanPicker2024ToolbarComponent {
  private readonly defaultSizeOptions: SelectOption[] = Object.values(PricingCompanySizeE).map(
    value => ({
      label: this.i18n.translate(
        value !== PricingCompanySizeE['151+']
          ? 'app.pricing.plan_picker_2024.toolbar.employee_option'
          : 'app.pricing.plan_picker_2024.toolbar.employee_option_and_more',
        {
          value,
        },
      ),
      value,
    }),
  );
  public readonly ANNUAL_DISCOUNT = 20;

  @Input() public enableCurrencyChange: boolean;
  @Input() public enableIntervalChange: boolean;
  @Input() public companySize: PricingCompanySize;
  @Input() public minimalCompanySize: PricingCompanySize | null | undefined;
  @Input() public sizeSelectMode: SizeSelectMode;
  @Input() public interval: PaymentInterval;
  @Input() public currency: Currency;

  @Output() public currencyChange = new EventEmitter<Currency>();
  @Output() public intervalChange = new EventEmitter<PaymentInterval>();
  @Output() public companySizeChange = new EventEmitter<PricingCompanySize>();

  public currencyButtons: Array<SegmentedButtonType<Currency>> = [
    {
      label: '€ EUR',
      value: Currency.EUR,
      isActive: true,
      isAvailable: true,
    },
    {
      label: '$ USD',
      value: Currency.USD,
      isActive: false,
      isAvailable: true,
    },
    {
      label: '£ GBP',
      value: Currency.GBP,
      isActive: false,
      isAvailable: true,
    },
  ];

  public companySizeOptions: SelectOption[] = [];
  public SizeSelectMode = SizeSelectMode;

  constructor(
    private i18n: I18nService,
    private intercom: IntercomService,
    private cdRef: ChangeDetectorRef,
  ) {}

  public ngOnChanges(changes: SimpleChanges) {
    if (this.minimalCompanySize !== undefined && !this.companySizeOptions.length) {
      this.buildSizeOptions();
    }

    if (changes.currency) {
      this.currencyButtons = this.currencyButtons.map(button => ({
        ...button,
        isActive: changes.currency.currentValue === button.value,
      }));
      this.cdRef.detectChanges();
    }
  }

  public get intervalChecked() {
    return this.interval === 12;
  }

  public get isMonthly() {
    return this.interval === 1;
  }

  public get isAnnually() {
    return this.interval === 12;
  }

  public changeInterval() {
    const interval = this.interval === 12 ? 1 : 12;
    this.intervalChange.emit(interval);
  }

  public trackByOption(index: number) {
    return index;
  }

  public openIntercom() {
    this.intercom.open();
  }

  private buildSizeOptions() {
    switch (this.sizeSelectMode) {
      case SizeSelectMode.Default:
        this.companySizeOptions = this.getOptionsForDefaultMode();
        break;
      case SizeSelectMode.Locked:
        this.companySizeOptions = this.getOptionsForSizeLockedMode();
        break;
      case SizeSelectMode.Agency:
        this.companySizeOptions = this.getOptionsForAgencyMode();
        break;
      case SizeSelectMode.LockedMinimalMissing:
      default:
        this.companySizeOptions = [...this.defaultSizeOptions];
        break;
    }
  }

  private getOptionsForDefaultMode() {
    let options: SelectOption[];
    const minimalSizeIndex = this.defaultSizeOptions.findIndex(
      option => option.value === this.minimalCompanySize,
    );
    if (minimalSizeIndex >= 0) {
      options = this.defaultSizeOptions.slice(minimalSizeIndex);
    } else {
      options = [
        {
          value: this.companySize,
          label: this.i18n.translate(
            'app.pricing.plan_picker_2024.toolbar.employee_option_and_more',
            {
              value: PricingCompanySizeE['151+'],
            },
          ),
        },
      ];
    }
    return options;
  }

  private getOptionsForSizeLockedMode() {
    const options = [...this.defaultSizeOptions];
    if (!this.defaultSizeOptions.find(option => option.value === this.companySize)) {
      // add item with a bigger range if it wasn't there initially
      options.push({
        value: this.companySize,
        label: this.i18n.translate('app.pricing.plan_picker_2024.toolbar.employee_option', {
          value: this.companySize,
        }),
      });
    }
    return options;
  }

  private getOptionsForAgencyMode(): SelectOption[] {
    return [
      {
        label: this.i18n.translate('app.pricing.plan_picker_2024.toolbar.agency_option'),
        value: PricingCompanySizeE['1-20'],
      },
    ];
  }
}
